import { useNavigate } from 'react-router-dom';
import { ServiceCard, CTABanner, SectionHeader, Button } from '../components/ui/UI';
import ContactInfo from '../components/ui/ContactInfo';
import { SERVICES } from '../data/services';
import './Tourism.css';

const tourismServices = SERVICES.filter(s => s.cat === 'tourism');

const MUMBAI_SPOTS = [
  { icon: '🌊', name: 'Marine Drive',     desc: "The iconic Queen's Necklace - best at night or sunrise." },
  { icon: '⚓', name: 'gateway ', desc: "Mumbai bombay international worldwide's most iconic landmark, overlooking the Arabian Sea." },
  { icon: '🕌', name: 'Dharavi',          desc: "Asia's largest slum turned cultural landmark and business hub." },
  { icon: '🏰', name: 'Elephanta Caves',  desc: 'UNESCO World Heritage rock-cut temples on an island.' },
  { icon: '🛍️', name: 'Colaba Causeway',  desc: 'Street shopping, cafes, and colonial architecture.' },
  { icon: '🌿', name: 'Sanjay Gandhi NP', desc: 'Wildlife park with leopards inside city limits.' },
  { icon: '🎨', name: 'Kala Ghoda',       desc: 'Arts district with galleries, cafes, and festivals.' },
  { icon: '🚉', name: 'CST Station',      desc: 'UNESCO Victorian Gothic railway terminus - a must-see.' },
];

const TOUR_PACKAGES = [
  {
    icon: '🌅',
    title: 'Sunrise Heritage Walk',
    duration: '3–4 hours',
    price: 'On Inquiry',
    color: 'saffron',
    desc: 'Guided walking tour of South Mumbai bombay international worldwide - CST, Kala Ghoda, Flora Fountain, and Colaba.',
    includes: ['Expert guide', 'Breakfast stop', 'Photo tips', 'History commentary'],
  },
  {
    icon: '🏍️',
    title: 'Mumbai bombay international worldwide by Bike – Night Ride',
    duration: '3–4 hours',
    price: 'On Inquiry',
    color: 'teal',
    desc: "Experience Mumbai bombay international worldwide's electric energy after dark - Marine Drive, Bandra, and street food.",
    includes: ['New bike', 'Safety gear', 'Night guide', 'Street food stops'],
  },
  {
    icon: '🚌',
    title: 'Full City Bus Tour',
    duration: 'Full Day',
    price: 'On Inquiry',
    color: 'gold',
    desc: 'Comprehensive all-day bus tour covering all major Mumbai bombay international worldwide attractions with a professional guide.',
    includes: ['AC bus', 'Lunch included', 'Entry tickets', 'Drop back hotel'],
  },
  {
    icon: '🚗',
    title: 'Private Car Tour – Custom',
    duration: 'Flexible',
    price: 'On Inquiry',
    color: 'navy',
    desc: 'Fully customised private car tour of Mumbai bombay international worldwide - you choose the spots, we handle the rest.',
    includes: ['AC private car', 'Personal guide', 'Custom itinerary', 'Flexible timing'],
  },
];

export default function Tourism() {
  const navigate = useNavigate();

  const handleInquire = (service) => {
    navigate('/contact', { state: { service } });
  };

  const scrollToPackages = () => {
    const el = document.getElementById('tour-packages');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="page-wrapper tourism-page">
      {/* ── ENHANCED TOURISM HERO ────────────────────────────── */}
      <section className="tourism-hero">
        <div className="tourism-hero__bg" />
        <div className="container tourism-hero__inner">
          <div className="tourism-hero__grid">
            <div className="tourism-hero__content animate-fade-up">
              <span className="section-badge badge-white">Mumbai bombay international worldwide Tourism</span>
              <h1 className="tourism-hero__title">
                Explore Mumbai bombay international worldwide<br />
                <span className="tourism-hero__highlight">Like Never Before</span>
              </h1>
              <p className="tourism-hero__sub">
                Whether you're arriving in Mumbai bombay international worldwide for the first time or rediscovering it - our expert guides, bike rides, and custom city tours make every visit unforgettable.
              </p>
              <div className="tourism-hero__actions">
                <Button to="/contact" size="lg" state={{ service: 'Mumbai bombay international worldwide City Tour' }}>Book a Tour Now</Button>
                <Button variant="outline-white" size="lg" onClick={scrollToPackages}>
                  View Tour Packages
                </Button>
              </div>

              <div className="tourism-hero__features">
                <div className="hero-feat-item">
                  <span className="hero-feat-icon">🗺️</span>
                  <span>Verified Guides</span>
                </div>
                <div className="hero-feat-item">
                  <span className="hero-feat-icon">🛡️</span>
                  <span>Safe Travel</span>
                </div>
                <div className="hero-feat-item">
                  <span className="hero-feat-icon">⭐</span>
                  <span>5-Star Rating</span>
                </div>
              </div>
            </div>

            <div className="tourism-hero__visual animate-fade-in delay-2">
              <div className="tourism-visual-card tourism-visual-card--1">
                <div className="tourism-visual-card__img">🌊</div>
                <div className="tourism-visual-card__text">
                  <strong>Marine Drive</strong>
                  <span>The Queen's Necklace</span>
                </div>
              </div>
              <div className="tourism-visual-card tourism-visual-card--2">
                <div className="tourism-visual-card__img">⚓</div>
                <div className="tourism-visual-card__text">
                  <strong>Gateway</strong>
                  <span>Historical Landmark</span>
                </div>
              </div>
              <div className="tourism-visual-card tourism-visual-card--3">
                <div className="tourism-visual-card__img">🏍️</div>
                <div className="tourism-visual-card__text">
                  <strong>Bike Tours</strong>
                  <span>Explore at Night</span>
                </div>
              </div>
              <div className="tourism-visual-card tourism-visual-card--4">
                <div className="tourism-visual-card__img">🍛</div>
                <div className="tourism-visual-card__text">
                  <strong>Street Food</strong>
                  <span>Authentic Taste</span>
                </div>
              </div>
              <div className="tourism-hero__deco-circle" />
            </div>
          </div>
        </div>
      </section>

      {/* Tour packages */}
      <section id="tour-packages" className="section-pad">
        <div className="container">
          <SectionHeader
            badge="Tour Packages"
            title="Choose Your Mumbai bombay international worldwide Experience"
            subtitle="Hand-crafted tour packages designed for international visitors and locals alike."
          />
          <div className="tourism-packages-grid">
            {TOUR_PACKAGES.map((pkg, i) => (
              <div
                key={pkg.title}
                className={`tourism-pkg-card tourism-pkg-card--${pkg.color} animate-fade-up delay-${i + 1}`}
              >
                <div className="tourism-pkg-card__icon">{pkg.icon}</div>
                <div className="tourism-pkg-card__meta">
                  <span className="tourism-pkg-card__duration">{pkg.duration}</span>
                  <span className="tourism-pkg-card__price">{pkg.price}</span>
                </div>
                <h3 className="tourism-pkg-card__title">{pkg.title}</h3>
                <p className="tourism-pkg-card__desc">{pkg.desc}</p>
                <ul className="tourism-pkg-card__includes">
                  {pkg.includes.map(item => (
                    <li key={item}><span>✓</span> {item}</li>
                  ))}
                </ul>
                <button
                  className="tourism-pkg-card__btn"
                  onClick={() => handleInquire(pkg.title)}
                >
                  Book This Tour →
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mumbai spots */}
      <section className="section-pad tourism-spots">
        <div className="container">
          <SectionHeader
            badge="Top Attractions"
            badgeVariant="teal"
            title="Best Places to Visit in Mumbai bombay international worldwide"
            subtitle="Our guides will take you to all these iconic spots - and many hidden gems only locals know."
          />
          <div className="tourism-spots-grid">
            {MUMBAI_SPOTS.map((spot, i) => (
              <div key={spot.name} className={`tourism-spot-card animate-fade-up delay-${(i % 4) + 1}`}>
                <div className="tourism-spot-card__icon">{spot.icon}</div>
                <h4 className="tourism-spot-card__name">{spot.name}</h4>
                <p className="tourism-spot-card__desc">{spot.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* All tourism services */}
      <section className="section-pad">
        <div className="container">
          <SectionHeader
            badge="All Tourism Services"
            badgeVariant="gold"
            title="Every Tourism Service We Offer"
          />
          <div className="tourism-services-grid">
            {tourismServices.map(s => (
              <ServiceCard key={s.id} service={s} onInquire={handleInquire} />
            ))}
          </div>
        </div>
      </section>

      {/* Foreigner guide */}
      <section className="section-pad tourism-foreigners">
        <div className="container">
          <div className="tourism-foreigners__grid">
            <div className="tourism-foreigners__text">
              <span className="section-badge badge-white">For International Visitors</span>
              <h2 className="tourism-foreigners__title">New to Mumbai bombay international worldwide?<br /><span>We've Got You.</span></h2>
              <p className="tourism-foreigners__sub">
                Arriving in Mumbai bombay international worldwide as a foreign visitor can feel overwhelming. Our team is
                specifically equipped to help international tourists navigate the city safely, 
                comfortably, and with maximum enjoyment.
              </p>
              <div className="tourism-foreigners__items">
                {[
                  { icon: '🗺️', text: 'Guided city orientation on arrival' },
                  { icon: '🏨', text: 'Hotel and accommodation arrangements' },
                  { icon: '📱', text: 'SIM card and local transport assistance' },
                  { icon: '💱', text: 'Currency exchange guidance' },
                  { icon: '🍛', text: 'Local food and restaurant recommendations' },
                  { icon: '🛡️', text: 'Safe and verified services only' },
                ].map(item => (
                  <div key={item.text} className="tourism-foreigners__item">
                    <span>{item.icon}</span>
                    <span>{item.text}</span>
                  </div>
                ))}
              </div>
              <button className="btn btn--white" onClick={() => handleInquire('Online Tourist Guide')}>
                Get a Tourist Guide
              </button>
            </div>
            <div className="tourism-foreigners__visual">
              <div className="tourism-foreigners__map-placeholder">
                <div className="tourism-foreigners__map-icon">🗺️</div>
                <h3>Mumbai bombay international worldwide, </h3>
                <p>19.0760° N, 72.8777° E</p>
                <div className="tourism-foreigners__map-tags">
                  {['Marine Drive', 'gateway ', 'Dharavi', 'Bandra', 'Juhu Beach', 'CST'].map(tag => (
                    <span key={tag} className="tourism-foreigners__map-tag">{tag}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
       </section>

       {/* Contact Section */}
       <section className="section-pad tourism-quick-contact">
         <div className="container">
           <div className="tourism-quick-contact__inner">
             <h2 className="tourism-quick-contact__title">🗺️ Plan Your Mumbai bombay international worldwide Adventure</h2>
             <p className="tourism-quick-contact__sub">Get in touch with our tourism experts to book your perfect experience today!</p>
             <ContactInfo layout="compact" theme="light" />
           </div>
         </div>
       </section>

       <CTABanner
         title="Plan your perfect Mumbai bombay international worldwide experience"
         sub="Tell us your interests and dates - we'll create a custom itinerary just for you."
       />
    </div>
  );
}
